<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import { FileText } from 'lucide-vue-next';
import type { Document } from '@/types/ui/PromptManager';
import { aiService } from '@/services/AIService';
import PlatformDocumentSelector from '@/components/ui/document/PlatformDocumentSelector.vue';
import PlaygroundDocumentSelector from '@/components/ui/document/PlaygroundDocumentSelector.vue';

interface Props {
  selectedDocument: Document | null;
}

defineProps<Props>();

const emit = defineEmits<{
  documentSelect: [document: Document | null];
}>();

const currentTab = ref<string>('platform');
const uploadDocuments = ref<Document[]>([]);

const handleDocumentSelect = (document: Document | null) => {
  emit('documentSelect', document);
};

const handleUploadDocumentsUpdate = (documents: Document[]) => {
  uploadDocuments.value = documents;
};

// Pre-fetch upload documents on mount
onMounted(async () => {
  try {
    uploadDocuments.value = await aiService.getAllDocuments();
  } catch (error) {
    console.error('Failed to pre-fetch upload documents:', error);
  }
});
</script>

<template>
  <Card class="tw:h-fit tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
    <CardHeader>
      <CardTitle class="tw:flex tw:items-center tw:gap-2">
        <FileText class="tw:h-5 tw:w-5 tw:text-blue-600" />
        Documents
      </CardTitle>
      <CardDescription> Select a document for processing </CardDescription>
    </CardHeader>
    <CardContent>
      <TabsRoot
        v-model="currentTab"
        class="tw:w-full">
        <TabsList class="tw:inline-flex tw:h-10 tw:w-fit tw:items-center tw:justify-center tw:rounded-md tw:bg-slate-100 tw:p-1 tw:text-slate-600">
          <TabsTrigger
            value="platform"
            class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            Platform Documents
          </TabsTrigger>
          <TabsTrigger
            value="upload"
            class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            Playground Documents
          </TabsTrigger>
        </TabsList>

        <!-- Platform Documents Tab Content -->
        <TabsContent
          value="platform"
          class="tw:mt-4">
          <PlatformDocumentSelector
            :selectedDocument="selectedDocument"
            @documentSelect="handleDocumentSelect" />
        </TabsContent>

        <!-- Upload Documents Tab Content -->
        <TabsContent
          value="upload"
          class="tw:mt-4">
          <PlaygroundDocumentSelector
            :selectedDocument="selectedDocument"
            :documents="uploadDocuments"
            @documentSelect="handleDocumentSelect"
            @update:documents="handleUploadDocumentsUpdate" />
        </TabsContent>
      </TabsRoot>
    </CardContent>
  </Card>
</template>
